import React, { useCallback, useState } from 'react';
import {
  View, Text, StyleSheet, FlatList, ActivityIndicator,
  RefreshControl, TouchableOpacity, Modal, Pressable,
  TextInput, Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import pilotService from '../services/pilotService';
import apiClient from '../utils/axiosConfig';

export default function PilotAircraftScreen() {
  const { user }  = useSelector(state => state.auth);
  const isIndependent = user?.is_pilot && !user?.pilot_has_company && !user?.is_drone_operator;

  const [aircraft, setAircraft]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [addModal, setAddModal]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [form, setForm] = useState({ name: '', brand: '', model: '', serial_number: '', weight: '' });

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const data = await pilotService.getMyAircraft();
      setAircraft(data || []);
    } catch (e) {
      // silencioso
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { load(); }, [load]));

  const createAircraft = async () => {
    if (!form.name || !form.brand || !form.model || !form.serial_number || !form.weight) {
      Alert.alert('Campos requeridos', 'Completa todos los campos.');
      return;
    }
    setSaving(true);
    try {
      await apiClient.post('/pilot-aircraft/create/', { ...form, weight: parseFloat(form.weight) });
      setAddModal(false);
      setForm({ name: '', brand: '', model: '', serial_number: '', weight: '' });
      await load(true);
    } catch (e) {
      Alert.alert('Error', e?.response?.data?.error || 'No se pudo crear la aeronave.');
    } finally {
      setSaving(false);
    }
  };

  const renderItem = ({ item }) => (
    <View style={styles.card}>
      <View style={styles.iconBox}>
        <Ionicons name="airplane" size={22} color="#1d4ed8" />
      </View>
      <View style={styles.info}>
        <Text style={styles.name}>{item.name}</Text>
        <Text style={styles.model}>{item.brand} {item.model}</Text>
        <Text style={styles.serial}>S/N: {item.serial_number}</Text>
      </View>
      <View style={styles.badge}>
        <Ionicons name="checkmark-circle" size={16} color="#16a34a" />
        <Text style={styles.badgeText}>Asignada</Text>
      </View>
    </View>
  );

  if (loading) return (
    <View style={styles.center}><ActivityIndicator size="large" color="#1d4ed8" /></View>
  );

  return (
    <View style={{ flex: 1 }}>
      <FlatList
        data={aircraft}
        keyExtractor={i => String(i.id)}
        renderItem={renderItem}
        style={styles.container}
        contentContainerStyle={{ padding: 16, paddingBottom: 100 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(true); }} tintColor="#2c3e50" />}
        ListEmptyComponent={
          <View style={styles.empty}>
            <Ionicons name="airplane-outline" size={44} color="#d1d5db" />
            <Text style={styles.emptyText}>Sin aeronaves</Text>
            {isIndependent
              ? <Text style={styles.emptySub}>Agrega tu primera aeronave con el botón +</Text>
              : <Text style={styles.emptySub}>Contacta al administrador de tu empresa para que te asigne una aeronave</Text>
            }
          </View>
        }
      />

      {/* FAB — solo para pilotos independientes */}
      {isIndependent && (
        <TouchableOpacity style={styles.fab} onPress={() => setAddModal(true)}>
          <Ionicons name="add" size={26} color="#fff" />
        </TouchableOpacity>
      )}

      {/* Modal nueva aeronave */}
      <Modal visible={addModal} transparent animationType="slide" onRequestClose={() => setAddModal(false)}>
        <Pressable style={styles.modalOverlay} onPress={() => setAddModal(false)}>
          <Pressable style={styles.modalBox} onPress={e => e.stopPropagation?.()}>
            <Text style={styles.modalTitle}>Nueva aeronave</Text>
            {[
              { key: 'name',          label: 'Nombre / Identificador', placeholder: 'ej. DJI T40 #1' },
              { key: 'brand',         label: 'Marca',                  placeholder: 'ej. DJI' },
              { key: 'model',         label: 'Modelo',                 placeholder: 'ej. Agras T40' },
              { key: 'serial_number', label: 'Número de serie',        placeholder: 'ej. 1ZNBC9F0D12345' },
              { key: 'weight',        label: 'Peso (kg)',              placeholder: 'ej. 24.5', keyboard: 'decimal-pad' },
            ].map(f => (
              <View key={f.key} style={styles.inputGroup}>
                <Text style={styles.inputLabel}>{f.label}</Text>
                <TextInput
                  style={styles.input}
                  placeholder={f.placeholder}
                  placeholderTextColor="#9ca3af"
                  keyboardType={f.keyboard || 'default'}
                  value={form[f.key]}
                  onChangeText={v => setForm(prev => ({ ...prev, [f.key]: v }))}
                />
              </View>
            ))}
            <TouchableOpacity style={styles.saveBtn} onPress={createAircraft} disabled={saving}>
              {saving
                ? <ActivityIndicator size="small" color="#fff" />
                : <Text style={styles.saveBtnText}>Guardar aeronave</Text>}
            </TouchableOpacity>
            <TouchableOpacity style={styles.cancelBtn} onPress={() => setAddModal(false)}>
              <Text style={styles.cancelBtnText}>Cancelar</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container:    { flex: 1, backgroundColor: '#f9fafb' },
  center:       { flex: 1, justifyContent: 'center', alignItems: 'center' },
  card:         { backgroundColor: '#fff', borderRadius: 14, padding: 14, marginBottom: 10, flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: '#f3f4f6', shadowColor: '#000', shadowOpacity: 0.05, shadowRadius: 4, elevation: 2 },
  iconBox:      { width: 42, height: 42, borderRadius: 12, backgroundColor: '#eff6ff', justifyContent: 'center', alignItems: 'center', marginRight: 12 },
  info:         { flex: 1 },
  name:         { fontSize: 15, fontWeight: '700', color: '#111827' },
  model:        { fontSize: 12, color: '#6b7280', marginTop: 2 },
  serial:       { fontSize: 11, color: '#9ca3af', marginTop: 1 },
  badge:        { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: '#f0fdf4', paddingHorizontal: 8, paddingVertical: 4, borderRadius: 20 },
  badgeText:    { fontSize: 11, color: '#16a34a', fontWeight: '600' },
  empty:        { alignItems: 'center', marginTop: 60, gap: 10, paddingHorizontal: 32 },
  emptyText:    { fontSize: 16, fontWeight: '600', color: '#374151' },
  emptySub:     { fontSize: 13, color: '#9ca3af', textAlign: 'center' },
  fab:          { position: 'absolute', bottom: 28, right: 20, width: 52, height: 52, borderRadius: 26, backgroundColor: '#1d4ed8', justifyContent: 'center', alignItems: 'center', shadowColor: '#1d4ed8', shadowOpacity: 0.4, shadowRadius: 8, elevation: 6 },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' },
  modalBox:     { backgroundColor: '#fff', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 24, paddingBottom: 36 },
  modalTitle:   { fontSize: 17, fontWeight: '700', color: '#111827', marginBottom: 16 },
  inputGroup:   { marginBottom: 12 },
  inputLabel:   { fontSize: 12, fontWeight: '600', color: '#374151', marginBottom: 4 },
  input:        { borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, color: '#111827', backgroundColor: '#f9fafb' },
  saveBtn:      { backgroundColor: '#1d4ed8', borderRadius: 10, padding: 14, alignItems: 'center', marginTop: 4 },
  saveBtnText:  { color: '#fff', fontWeight: '700', fontSize: 15 },
  cancelBtn:    { padding: 14, alignItems: 'center', marginTop: 4 },
  cancelBtnText:{ color: '#6b7280', fontSize: 14 },
});
